import React, { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { styled } from "@mui/material/styles";
import {
  ThemeProvider,
  createTheme,
  CircularProgress,
  TextField,
  Typography,
  Pagination,
  Grid,
  Container,
  Box,
  Button,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import "./CardDocumentos.css";
import CardItemDocumentos from "./CardItemDocumentos.js";

const theme = createTheme({
  palette: {
    warning: {
      main: "#ff9800",
      dark: "#f57c00",
    },
  },
});

const CustomTextField = styled(TextField)(({ theme: muiTheme }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: muiTheme.palette.warning.main,
    },
    "&:hover fieldset": {
      borderColor: muiTheme.palette.warning.dark,
    },
    "&.Mui-focused fieldset": {
      borderColor: muiTheme.palette.warning.main,
    },
  },
}));

const normalizeText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const normalizeForSearch = (value) =>
  normalizeText(value).replace(/[^\p{L}\p{N}\s]+/gu, " ").replace(/\s+/g, " ").trim();

const STOP_WORDS = new Set([
  "a", "o", "e", "de", "do", "da", "dos", "das", "em", "no", "na", "nos", "nas", "um", "uma", "uns", "umas",
  "ao", "aos", "à", "às", "com", "sem", "por", "para", "que", "se", "sua", "seu", "suas", "seus", "os", "as",
  "del", "de", "la", "las", "el", "los", "y", "en", "con", "sin", "por", "para", "que", "una", "uno", "unos",
]);

const extractRelevantTokens = (query) => {
  const baseTokens = normalizeForSearch(query).split(" ").filter((token) => token.length >= 3);
  const filtered = baseTokens.filter((token) => !STOP_WORDS.has(token));
  if (filtered.length > 0) return [...new Set(filtered)];
  return [...new Set(baseTokens)];
};

const mapVerbetesComDocumento = (items) =>
  (Array.isArray(items) ? items : [])
    .filter((item) => Boolean(item?.linkpdfdoclink))
    .map((item) => ({
      ...item,
      __tipo: "verbete",
    }));

const mapDocumentosAvulsos = (items) =>
  (Array.isArray(items) ? items : [])
    .filter((item) => Boolean(item?.linkpdfdoclink))
    .map((item) => ({
      ...item,
      __tipo: "avulso",
    }));

const fetchVerbetesComDocumento = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || "/api";
  const response = await fetch(`${apiUrl}/verbetes/`);
  if (!response.ok) throw new Error("Erro ao carregar documentos de verbetes.");
  const data = await response.json();
  return mapVerbetesComDocumento(data);
};

const fetchDocumentosAvulsos = async () => {
  const apiUrl = process.env.REACT_APP_API_URL || "/api";
  const response = await fetch(`${apiUrl}/documentos`);
  if (response.status === 404) return [];
  if (!response.ok) throw new Error("Erro ao carregar documentos avulsos.");
  const data = await response.json();
  return mapDocumentosAvulsos(data);
};

const mapJsonPages = (payload) => {
  if (!Array.isArray(payload?.pages)) return [];
  return payload.pages
    .map((item, idx) => ({
      pageNumber: Number(item?.page) || idx + 1,
      text: String(item?.text || ""),
    }))
    .filter((page) => page.text.trim().length > 0);
};

const buildJsonIndexCandidates = (doc) => {
  const candidates = [];
  if (doc?.linkjsondocindex && typeof doc.linkjsondocindex === "string") {
    candidates.push(doc.linkjsondocindex);
  }

  if (doc?.linkpdfdoclink && typeof doc.linkpdfdoclink === "string") {
    const base = doc.linkpdfdoclink.replace(/\.pdf(\?.*)?$/i, "");
    candidates.push(`${base}_normalizado.json`);
  }

  return [...new Set(candidates.filter(Boolean))];
};

const buildSnippet = (text, maxLen = 220) => {
  const compact = String(text || "").replace(/\s+/g, " ").trim();
  return compact.length > maxLen ? `${compact.slice(0, maxLen)}...` : compact;
};

const buildDocRoute = (item, query, page) => {
  const basePath =
    item?.__tipo === "avulso"
      ? `/DocumentoAvulso/${item._id}`
      : `/DocumentoDoVerbete/${item._id}`;

  if (!query) return basePath;
  const params = new URLSearchParams();
  params.set("q", query);
  if (page) params.set("page", String(page));
  return `${basePath}?${params.toString()}`;
};

const getBestMatch = (doc, pages, queryRaw) => {
  const query = normalizeForSearch(queryRaw);
  if (!query) return null;

  const tokens = extractRelevantTokens(queryRaw);
  if (tokens.length === 0) return null;
  let bestMatch = null;

  pages.forEach((page) => {
    const normalizedPage = normalizeForSearch(page.text);
    if (!normalizedPage) return;

    const exact = normalizedPage.includes(query);
    const tokenMatches = tokens.reduce((acc, token) => (normalizedPage.includes(token) ? acc + 1 : acc), 0);
    const coverage = tokenMatches / tokens.length;

    if (!exact) {
      const minMatches = tokens.length >= 8 ? 4 : tokens.length >= 4 ? 3 : 1;
      const minCoverage = tokens.length >= 6 ? 0.45 : tokens.length >= 3 ? 0.5 : 1;
      if (tokenMatches < minMatches || coverage < minCoverage) return;
    }

    const score = exact ? 1000 + tokenMatches * 20 : tokenMatches * 40 + Math.round(coverage * 100);
    if (!bestMatch || score > bestMatch.score || (score === bestMatch.score && page.pageNumber < bestMatch.page)) {
      bestMatch = {
        _id: doc._id,
        __tipo: doc.__tipo,
        titulo: doc.titulo,
        autor: doc.autor,
        imgcard: doc.imgcard,
        page: page.pageNumber,
        score,
        snippet: buildSnippet(page.text),
      };
    }
  });

  return bestMatch;
};

const Documentos = () => {
  const [searchText, setSearchText] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchProgress, setSearchProgress] = useState({ done: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const indexCacheRef = useRef(new Map());
  const inFlightCacheRef = useRef(new Map());

  const {
    data: verbetesDocs = [],
    isLoading: isLoadingVerbetes,
    error: errorVerbetes,
  } = useQuery({
    queryKey: ["documentos-verbetes"],
    queryFn: fetchVerbetesComDocumento,
  });

  const {
    data: documentosAvulsos = [],
    isLoading: isLoadingAvulsos,
    error: errorAvulsos,
  } = useQuery({
    queryKey: ["documentos-avulsos"],
    queryFn: fetchDocumentosAvulsos,
  });

  const data = useMemo(() => {
    const merged = [...verbetesDocs, ...documentosAvulsos];
    merged.sort((a, b) => normalizeText(a.titulo).localeCompare(normalizeText(b.titulo)));
    return merged;
  }, [verbetesDocs, documentosAvulsos]);

  const fetchJsonWithTimeout = async (url, timeoutMs = 12000) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetch(url, {
        cache: "no-store",
        signal: controller.signal,
      });
      return response;
    } finally {
      clearTimeout(timeout);
    }
  };

  const loadDocumentIndex = async (doc) => {
    const cacheKey = `${String(doc?.__tipo || "")}:${String(doc?._id || "")}`;
    if (!cacheKey || cacheKey === ":") return [];

    if (indexCacheRef.current.has(cacheKey)) {
      return indexCacheRef.current.get(cacheKey);
    }
    if (inFlightCacheRef.current.has(cacheKey)) {
      return inFlightCacheRef.current.get(cacheKey);
    }

    const loadPromise = (async () => {
      const candidates = buildJsonIndexCandidates(doc);
      for (const indexUrl of candidates) {
        try {
          const response = await fetchJsonWithTimeout(indexUrl);
          if (!response.ok) continue;
          const payload = await response.json();
          const mappedPages = mapJsonPages(payload);
          if (mappedPages.length > 0) {
            indexCacheRef.current.set(cacheKey, mappedPages);
            return mappedPages;
          }
        } catch (indexError) {
          console.error("Erro ao carregar índice JSON:", indexUrl, indexError);
        }
      }

      indexCacheRef.current.set(cacheKey, []);
      return [];
    })();

    inFlightCacheRef.current.set(cacheKey, loadPromise);
    try {
      return await loadPromise;
    } finally {
      inFlightCacheRef.current.delete(cacheKey);
    }
  };

  const runGlobalSearch = async () => {
    const rawQuery = searchText.trim();
    if (!rawQuery) {
      setActiveQuery("");
      setSearchResults([]);
      setSearchError("");
      setCurrentPage(1);
      return;
    }

    if (normalizeForSearch(rawQuery).length < 3) {
      setSearchError("Digite pelo menos 3 caracteres para buscar no conteúdo dos documentos.");
      setActiveQuery("");
      setSearchResults([]);
      return;
    }

    setSearching(true);
    setSearchError("");

    try {
      const docs = Array.isArray(data) ? data : [];
      const results = [];
      const concurrency = 4;
      let done = 0;
      setSearchProgress({ done: 0, total: docs.length });

      for (let i = 0; i < docs.length; i += concurrency) {
        const batch = docs.slice(i, i + concurrency);
        const batchResults = await Promise.all(
          batch.map(async (doc) => {
            const pages = await loadDocumentIndex(doc);
            if (pages.length === 0) return null;
            return getBestMatch(doc, pages, rawQuery);
          })
        );
        batchResults.forEach((result) => {
          if (result) results.push(result);
        });
        done += batch.length;
        setSearchProgress({ done, total: docs.length });
      }

      results.sort((a, b) => b.score - a.score || normalizeText(a.titulo).localeCompare(normalizeText(b.titulo)));
      setSearchResults(results);
      setActiveQuery(rawQuery);
      setCurrentPage(1);
      if (results.length === 0) {
        setSearchError("Nenhuma ocorrência encontrada nos índices disponíveis.");
      }
    } catch (globalError) {
      console.error("Erro na busca global de documentos:", globalError);
      setSearchError("Falha ao buscar no conteúdo dos documentos.");
      setActiveQuery("");
      setSearchResults([]);
    } finally {
      setSearching(false);
      setSearchProgress((prev) => ({ ...prev, done: prev.total }));
    }
  };

  const sourceList = activeQuery ? searchResults : data;
  const totalPages = Math.max(1, Math.ceil(sourceList.length / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedData = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return sourceList.slice(start, start + itemsPerPage);
  }, [sourceList, safePage]);

  const isInitialLoading = isLoadingVerbetes || isLoadingAvulsos || searching;
  const hasBlockingError = sourceList.length === 0 && (errorVerbetes || errorAvulsos);

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <Box sx={{ my: 6 }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={8}>
              <CustomTextField
                multiline
                minRows={3}
                label="Buscar palavra nos documentos (conteúdo completo)"
                placeholder="Digite uma palavra ou frase"
                fullWidth
                color="warning"
                value={searchText}
                onChange={(event) => {
                  setSearchText(event.target.value);
                  setSearchError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    runGlobalSearch();
                  }
                }}
                helperText={searchError}
                error={Boolean(searchError)}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Button
                variant="contained"
                fullWidth
                onClick={runGlobalSearch}
                disabled={searching || isLoadingVerbetes || isLoadingAvulsos}
                sx={{
                  py: 1.3,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                <SearchIcon sx={{ mr: 1 }} />
                {searching ? "Buscando em todos os documentos..." : "Buscar no conteúdo"}
              </Button>
            </Grid>
            {activeQuery && (
              <Grid item xs={12} md={8}>
                <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
                  Resultados para: <strong>{activeQuery}</strong> ({searchResults.length} documento(s))
                </Typography>
              </Grid>
            )}
            {searching && searchProgress.total > 0 && (
              <Grid item xs={12} md={8}>
                <Typography variant="body2" sx={{ textAlign: "center", color: "text.secondary" }}>
                  Indexando documentos: {searchProgress.done}/{searchProgress.total}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box sx={{ my: 4 }}>
          <Grid container spacing={3}>
            {isInitialLoading ? (
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
              </Box>
            ) : hasBlockingError ? (
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", my: 4 }}>
                <Typography variant="h6" color="error">
                  Erro ao carregar documentos.
                </Typography>
              </Box>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((item) => (
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6} key={`${item.__tipo}-${item._id}`}>
                  <CardItemDocumentos
                    id={item._id}
                    title={item.titulo}
                    author={
                      activeQuery
                        ? `${item.autor || "Autor não informado"} | ${item.__tipo === "avulso" ? "Avulso" : "Verbete"} | Página ${item.page}`
                        : item.autor
                    }
                    imgcard={item.imgcard}
                    showLink
                    to={buildDocRoute(item, activeQuery, item.page)}
                  />
                  {activeQuery && item.snippet && (
                    <Typography sx={{ mt: 1, px: 1, fontSize: "0.9rem", color: "text.secondary" }}>
                      {item.snippet}
                    </Typography>
                  )}
                </Grid>
              ))
            ) : (
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", my: 4 }}>
                <Typography variant="h6" color="textSecondary">
                  {activeQuery ? "Nenhum documento contém essa palavra." : "Nenhum documento encontrado."}
                </Typography>
              </Box>
            )}
          </Grid>
        </Box>

        {!isInitialLoading && !hasBlockingError && sourceList.length > itemsPerPage && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <Pagination
              count={totalPages}
              page={safePage}
              onChange={(_, page) => setCurrentPage(page)}
              color="warning"
            />
          </Box>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default Documentos;
